import { addDoc, collection, doc, writeBatch } from 'firebase/firestore'
import {
  COLLECTIONS,
  type ClassifiedRow,
  type BudgetCode,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'
import { nowIso } from '@/lib/firestore'

/**
 * Committing a CSIU import — REQUIREMENTS.md §6.4 step 6, workflow §7.3.
 *
 * Three things happen together: rows classified `new` become transactions,
 * rows matched to an existing pending transaction mark it confirmed, and one
 * `csiuImports` record is written for the audit trail and the dashboard's
 * per-code freshness.
 *
 * They are batched. A partial commit is the worst outcome available here — it
 * would leave some rows imported and others not, with no way to tell which,
 * and re-running the import would then double-count everything that did land.
 */

/** What the review screen decided for one row. */
export interface RowDecision {
  classified: ClassifiedRow
  /** Unchecked rows are left alone entirely. */
  include: boolean
}

export interface ImportCommit {
  decisions: readonly RowDecision[]
  /** Which budget code each file's rows belong to, by account code. */
  codesByAccountCode: ReadonlyMap<string, BudgetCode>
  filenames: readonly string[]
  fiscalYear: number
}

export interface ImportResult {
  importId: string
  created: number
  confirmed: number
  skipped: number
}

/**
 * Firestore caps a batch at 500 writes. A year of imports across ten codes can
 * exceed that, so writes are chunked — but the audit record is written last,
 * after every chunk has landed, so its counts can never describe an import
 * that only partly happened.
 */
const MAX_WRITES_PER_BATCH = 450

export async function commitCsiuImport(
  userId: string,
  input: ImportCommit,
): Promise<ImportResult> {
  const timestamp = new Date()
  const operations: ((batch: ReturnType<typeof writeBatch>) => void)[] = []

  let created = 0
  let confirmed = 0
  let skipped = 0
  const codesTouched = new Set<string>()

  for (const { classified, include } of input.decisions) {
    const { row, classification, matchedTransactionId } = classified

    if (!include || classification === 'skip_pcard') {
      skipped += 1
      continue
    }

    const code =
      row.accountCode === null
        ? undefined
        : input.codesByAccountCode.get(row.accountCode)
    if (code?.id === undefined) {
      // No budget code for this account code. Counted rather than guessed at:
      // filing a charge against the wrong code is worse than not filing it.
      skipped += 1
      continue
    }
    codesTouched.add(code.id)

    if (
      (classification === 'duplicate_exact' ||
        classification === 'duplicate_potential') &&
      matchedTransactionId !== undefined
    ) {
      // §5.3: the same document is confirmed, not duplicated. Creating a second
      // row for one real-world purchase is exactly what the Sheets system does
      // and what this system exists to stop.
      operations.push((batch) =>
        batch.update(doc(db, COLLECTIONS.transactions, matchedTransactionId), {
          status: 'reconciled',
          reconciledVia: 'csiu',
          reconciledAt: timestamp,
          csiuJournalNumber: row.journalNumber === '' ? null : row.journalNumber,
          sourceRef: row.transactionNumber,
          updatedAt: timestamp,
        }),
      )
      confirmed += 1
      continue
    }

    const transactionDate =
      row.transactionDate === null ? timestamp : new Date(row.transactionDate)

    operations.push((batch) =>
      batch.set(doc(collection(db, COLLECTIONS.transactions)), {
        userId,
        budgetCodeId: code.id,
        fiscalYear: input.fiscalYear,
        source: 'csiu',
        sourceRef: row.transactionNumber,
        transactionDate,
        dateReceived: transactionDate,
        amount: row.amount,
        vendor: row.vendor ?? row.description ?? 'Unknown',
        description: row.description,
        // Arriving in a CSIU export *is* the confirmation for these sources —
        // the accounting system has already posted them.
        status: 'reconciled',
        reconciledAt: timestamp,
        reconciledVia: 'csiu',
        csiuJournalNumber: row.journalNumber === '' ? null : row.journalNumber,
        receiptIds: [],
        splitGroupId: null,
        reversalOfTransactionId: null,
        notes: '',
        noReceiptReason: null,
        noReceiptExplanation: null,
        createdAt: timestamp,
        updatedAt: timestamp,
      }),
    )
    created += 1
  }

  for (let i = 0; i < operations.length; i += MAX_WRITES_PER_BATCH) {
    const batch = writeBatch(db)
    for (const apply of operations.slice(i, i + MAX_WRITES_PER_BATCH)) apply(batch)
    await batch.commit()
  }

  const audit = await addDoc(collection(db, COLLECTIONS.csiuImports), {
    userId,
    importedAt: timestamp,
    filenames: [...input.filenames],
    rowsImported: created + confirmed,
    rowsSkipped: skipped,
    rowsFlagged: input.decisions.filter(
      (d) => d.classified.classification === 'duplicate_potential',
    ).length,
    budgetCodesTouched: [...codesTouched],
    createdAt: nowIso(),
  })

  return { importId: audit.id, created, confirmed, skipped }
}
